import React, { useState, useEffect } from "react";
import { Select, Checkbox } from "antd";
import { forms } from "../../views/medicines/AddMedicine";
import { getCompanies } from "../../apis/company";
import { getMedicines } from "../../apis/medicine";
import { Company } from "../../interfaces/Company";
import { savePharmacyItem, updatePharmacyItem } from "../../apis/pharmacyItems";
import { PharmacyItem } from "../../interfaces/PharmacyItems";

const { Option } = Select;

const ITEM_TYPES = [
  { value: "medicine", label: "Medicine" },
  { value: "surgical", label: "Surgical" },
  { value: "implant", label: "Implant" },
];

type AddPharmacyItemProps = {
  closeModal: () => void;
  editableItem?: any;
};

const AddPharmacyItem: React.FC<AddPharmacyItemProps> = ({ closeModal, editableItem }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [medicineOptions, setMedicineOptions] = useState<{ value: number; label: string }[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const isEditMode = Boolean(editableItem?.id);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PharmacyItem>({
    item_type: "medicine",
    name: "",
    sku: "",
    barcode: "",
    hsn_code: "",
    molecule: "",
    content: "",
    company_id: "",
    medicine_id: null,
    form: "",
    strength: "",
    unit: "",
    pack_size: 1,
    description: "",
    is_active: true,
    is_consumable: true,
    is_batch_tracked: false,
    is_generic: false,
    is_chargeable: true,
    is_discountable: false,
    fda_category: "",
    uqc: "",
    company: "",
  });

  useEffect(() => {
    fetchCompanies();
    fetchMedicines();
  }, []);

  useEffect(() => {
    if (editableItem) {
      // Pre-seed the medicine option so the Select shows the name immediately,
      // before the async medicine search results arrive.
      if (editableItem.medicineId && editableItem.medicineName) {
        setMedicineOptions([{ value: editableItem.medicineId, label: editableItem.medicineName }]);
      }

      setFormData({
        item_type: editableItem.itemType ?? "medicine",
        name: editableItem.name ?? "",
        sku: editableItem.sku ?? "",
        barcode: editableItem.barcode ?? "",
        hsn_code: editableItem.hsnCode ?? "",
        molecule: editableItem.molecule ?? "",
        content: editableItem.content ?? "",
        company_id: editableItem.companyId ?? "",
        company: editableItem.company ?? "",
        medicine_id: editableItem.medicineId ?? null,
        form: editableItem.form ?? "",
        strength: editableItem.strength ?? "",
        unit: editableItem.unit ?? "",
        pack_size: editableItem.packSize ?? 1,
        description: editableItem.description ?? "",
        is_active: editableItem.isActive ?? true,
        is_consumable: editableItem.isConsumable ?? true,
        is_batch_tracked: editableItem.isBatchTracked ?? false,
        is_generic: editableItem.isGeneric ?? false,
        is_chargeable: editableItem.isChargeable ?? true,
        is_discountable: editableItem.isDiscountable ?? false,
        fda_category: editableItem.fdaCategory ?? "",
        uqc: editableItem.uqc ?? "",
      });
    } else {
      setFormData({
        item_type: "medicine",
        name: "",
        sku: "",
        barcode: "",
        hsn_code: "",
        molecule: "",
        content: "",
        company_id: "",
        company: "",
        medicine_id: null,
        form: "",
        strength: "",
        unit: "",
        pack_size: 1,
        description: "",
        is_active: true,
        is_consumable: true,
        is_batch_tracked: false,
        is_generic: false,
        is_chargeable: true,
        is_discountable: false,
        fda_category: "",
        uqc: "",
      });
    }
  }, [editableItem]);

  const fetchCompanies = () => {
    getCompanies({ per_page: "2000" }).then((res: any) => setCompanies(res.data));
  };

  const fetchMedicines = (search = "") => {
    getMedicines({ name: search, per_page: 50 }).then((res: any) => {
      const data = res?.data?.data ?? [];
      setMedicineOptions((prev) => {
        const incoming: { value: number; label: string }[] = data.map((m: any) => ({ value: m.id, label: m.name }));
        // Keep the pre-seeded selected option if it isn't in the new results
        const selected = prev.find((o) => o.value === formData.medicine_id && !incoming.some((i) => i.value === o.value));
        return selected ? [selected, ...incoming] : incoming;
      });
    }).catch(() => {});
  };

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const validateForm = () => {
    const e: { [key: string]: string } = {};
    if (!formData.name) e.name = "* Name is required";
    if (!formData.company_id) e.company_id = "* Company is required";
    if (!formData.unit) e.unit = "* Unit is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSave = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      if (isEditMode) {
        await updatePharmacyItem(editableItem.id, formData);
      } else {
        await savePharmacyItem(formData);
      }
      closeModal();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6";

  const isMedicine = formData.item_type === "medicine";

  return (
    <div>
      <div className="grid grid-cols-1 gap-4">

        {/* Item Type */}
        <div>
          <label className="block text-sm font-medium text-gray-900">Item Type</label>
          <Select
            style={{ width: "100%" }}
            value={formData.item_type}
            onChange={(val) => handleChange("item_type", val)}
            options={ITEM_TYPES}
          />
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-900">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className={inputClass}
          />
          {errors.name && <span className="text-red-500 text-sm">{errors.name}</span>}
        </div>

        {/* SKU / Barcode / HSN Code */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900">SKU</label>
            <input
              type="text"
              value={formData.sku}
              onChange={(e) => handleChange("sku", e.target.value)}
              className={inputClass}
            />
            {errors.sku && <span className="text-red-500 text-sm">{errors.sku}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">Barcode</label>
            <input
              type="text"
              value={formData.barcode}
              onChange={(e) => handleChange("barcode", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">HSN Code</label>
            <input
              type="text"
              value={formData.hsn_code}
              onChange={(e) => handleChange("hsn_code", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Unit / Pack Size */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900">Unit</label>
            <input
              type="text"
              value={formData.unit}
              onChange={(e) => handleChange("unit", e.target.value)}
              className={inputClass}
            />
            {errors.unit && <span className="text-red-500 text-sm">{errors.unit}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">Pack Size</label>
            <input
              type="number"
              min={1}
              value={formData.pack_size ?? 1}
              onChange={(e) => handleChange("pack_size", parseInt(e.target.value) || 1)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Company / Form */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900">Company</label>
            <Select
              style={{ width: "100%" }}
              value={formData.company_id || undefined}
              onChange={(value) => handleChange("company_id", value)}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>{company.name}</Option>
              ))}
            </Select>
            {errors.company_id && <span className="text-red-500 text-sm">{errors.company_id}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">Form</label>
            <Select
              placeholder="Select Form"
              value={formData.form || undefined}
              onChange={(val) => handleChange("form", val)}
              className="w-full"
              allowClear
            >
              {forms.map((f) => (
                <Option key={f.value} value={f.value}>{f.name}</Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Medicine fields — only for medicine type */}
        {isMedicine && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900">Molecule</label>
                <input
                  type="text"
                  value={formData.molecule}
                  onChange={(e) => handleChange("molecule", e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900">Strength</label>
                <input
                  type="text"
                  value={formData.strength}
                  onChange={(e) => handleChange("strength", e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">
                Content / Composition
              </label>
              <input
                type="text"
                value={formData.content}
                onChange={(e) => handleChange("content", e.target.value)}
                placeholder="e.g. Paracetamol (500mg) + Ibuprofen (400mg)"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">
                Link to Prescription Medicine
              </label>
              <Select
                style={{ width: "100%" }}
                value={formData.medicine_id ?? undefined}
                onChange={(val) => handleChange("medicine_id", val ?? null)}
                allowClear
                showSearch
                placeholder="Search medicine..."
                onSearch={fetchMedicines}
                optionFilterProp="label"
                options={medicineOptions}
              />
              <p className="text-xs text-gray-400 mt-1">
                Link to enable "restrict prescriptions to pharmacy" filtering.
              </p>
            </div>
          </>
        )}

        {/* GST / Regulatory */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900">FDA Category</label>
            <input
              type="text"
              value={formData.fda_category}
              onChange={(e) => handleChange("fda_category", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">UQC (GST)</label>
            <input
              type="text"
              value={formData.uqc}
              onChange={(e) => handleChange("uqc", e.target.value)}
              placeholder="e.g. NOS, BOX, PAC"
              className={inputClass}
            />
          </div>
        </div>

        {/* Flags */}
        <div className="flex flex-wrap gap-4 items-center">
          <Checkbox checked={formData.is_active} onChange={(e) => handleChange("is_active", e.target.checked)}>Active</Checkbox>
          <Checkbox checked={formData.is_consumable} onChange={(e) => handleChange("is_consumable", e.target.checked)}>Consumable</Checkbox>
          <Checkbox checked={formData.is_batch_tracked} onChange={(e) => handleChange("is_batch_tracked", e.target.checked)}>Batch Tracked</Checkbox>
          <Checkbox checked={formData.is_chargeable} onChange={(e) => handleChange("is_chargeable", e.target.checked)}>Chargeable</Checkbox>
          <Checkbox checked={formData.is_discountable} onChange={(e) => handleChange("is_discountable", e.target.checked)}>Discountable</Checkbox>
          {isMedicine && (
            <Checkbox checked={formData.is_generic} onChange={(e) => handleChange("is_generic", e.target.checked)}>Generic</Checkbox>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-900">Description</label>
          <textarea
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex gap-4 mt-4">
        <button
          onClick={onSave}
          type="button"
          disabled={loading}
          className="px-3 py-2 bg-primary hover:bg-hover text-white rounded-md disabled:opacity-50"
        >
          {loading ? (isEditMode ? "Updating..." : "Saving...") : isEditMode ? "Update" : "Save"}
        </button>
        <button
          onClick={closeModal}
          type="button"
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddPharmacyItem;
